/**
 * Comprehensive Error Tracking Service
 *
 * Centralized error handling, logging, and reporting system for NG Meet UI.
 * Provides structured error tracking with context, user data, and analytics.
 */

export interface ErrorContext {
  userId?: string;
  userEmail?: string;
  sessionId: string;
  timestamp: string;
  url: string;
  userAgent: string;
  component?: string;
  action?: string;
  meetingId?: string;
  roomToken?: string;
  additionalData?: Record<string, unknown>;
}

export interface ErrorReport {
  id: string;
  error: Error;
  context: ErrorContext;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'ui' | 'api' | 'auth' | 'meeting' | 'network' | 'unknown';
  recoverable: boolean;
  userImpact: 'none' | 'minor' | 'major' | 'blocking';
}

export type ErrorHandler = (report: ErrorReport) => void;

class ErrorTrackingService {
  private sessionId: string;
  private handlers: ErrorHandler[] = [];
  private errorQueue: ErrorReport[] = [];
  private isOnline: boolean = navigator.onLine;
  private maxQueueSize: number = 100;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.setupGlobalHandlers();
    this.setupNetworkMonitoring();
  }

  /**
   * Generate unique session ID for error tracking
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Set up global error handlers
   */
  private setupGlobalHandlers(): void {
    // Handle unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      // Ignore harmless ResizeObserver errors
      if (event.message?.includes('ResizeObserver loop')) {
        event.preventDefault(); // Prevent error overlay from showing
        return;
      }

      this.captureError(
        new Error(event.message),
        {
          component: 'Global',
          action: 'unhandled_error',
          additionalData: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            stack: event.error?.stack,
          },
        },
        'high',
        'unknown',
      );
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

      this.captureError(
        error,
        {
          component: 'Global',
          action: 'unhandled_promise_rejection',
          additionalData: {
            reason: event.reason,
            promise: event.promise,
          },
        },
        'high',
        'unknown',
      );
    });

    // Handle React error boundaries (will be called by error boundaries)
    (window as unknown as { __errorTracker: ErrorTrackingService }).__errorTracker = this;
  }

  /**
   * Set up network monitoring
   */
  private setupNetworkMonitoring(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushErrorQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Add error handler (e.g., for external services like Sentry)
   */
  addHandler(handler: ErrorHandler): void {
    this.handlers.push(handler);
  }

  /**
   * Remove error handler
   */
  removeHandler(handler: ErrorHandler): void {
    const index = this.handlers.indexOf(handler);
    if (index > -1) {
      this.handlers.splice(index, 1);
    }
  }

  /**
   * Capture and process an error
   */
  captureError(
    error: Error,
    context: Partial<ErrorContext> = {},
    severity: ErrorReport['severity'] = 'medium',
    category: ErrorReport['category'] = 'unknown',
    recoverable: boolean = true,
    userImpact: ErrorReport['userImpact'] = 'minor',
  ): string {
    const errorId = this.generateErrorId();

    const fullContext: ErrorContext = {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...context,
    };

    const report: ErrorReport = {
      id: errorId,
      error,
      context: fullContext,
      severity,
      category,
      recoverable,
      userImpact,
    };

    // Log to console (always)
    this.logToConsole(report);

    // Add to queue for processing
    this.addToQueue(report);

    // Process immediately if online
    if (this.isOnline) {
      this.processError(report);
    }

    return errorId;
  }

  /**
   * Capture API errors with additional context
   */
  captureApiError(
    error: Error,
    endpoint: string,
    method: string,
    statusCode?: number,
    responseData?: unknown,
    context: Partial<ErrorContext> = {},
  ): string {
    return this.captureError(
      error,
      {
        ...context,
        component: 'API',
        action: `${method} ${endpoint}`,
        additionalData: {
          endpoint,
          method,
          statusCode,
          responseData,
          ...context.additionalData,
        },
      },
      statusCode && statusCode >= 500 ? 'high' : 'medium',
      'api',
    );
  }

  /**
   * Capture authentication errors
   */
  captureAuthError(error: Error, authAction: string, context: Partial<ErrorContext> = {}): string {
    return this.captureError(
      error,
      {
        ...context,
        component: 'Auth',
        action: authAction,
      },
      'high',
      'auth',
      false,
      'blocking',
    );
  }

  /**
   * Capture meeting-related errors
   */
  captureMeetingError(
    error: Error,
    meetingId: string,
    meetingAction: string,
    context: Partial<ErrorContext> = {},
  ): string {
    return this.captureError(
      error,
      {
        ...context,
        component: 'Meeting',
        action: meetingAction,
        meetingId,
      },
      'high',
      'meeting',
      true,
      'major',
    );
  }

  /**
   * Capture UI component errors
   */
  captureComponentError(
    error: Error,
    componentName: string,
    action: string,
    context: Partial<ErrorContext> = {},
  ): string {
    return this.captureError(
      error,
      {
        ...context,
        component: componentName,
        action,
      },
      'medium',
      'ui',
    );
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log error to console with structured format
   */
  private logToConsole(report: ErrorReport): void {
    const { error, context, severity, category } = report;

    const logLevel = severity === 'critical' || severity === 'high' ? 'error' : 'warn';

    console.group(`🚨 Error Report [${severity.toUpperCase()}] - ${category}`);
    console[logLevel]('Error:', error);
    console.info('Context:', context);
    console.info('Report ID:', report.id);
    console.info('User Impact:', report.userImpact);
    console.info('Recoverable:', report.recoverable);
    console.groupEnd();
  }

  /**
   * Add error to queue for batch processing
   */
  private addToQueue(report: ErrorReport): void {
    this.errorQueue.push(report);

    // Limit queue size to prevent memory issues
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift(); // Remove oldest error
    }
  }

  /**
   * Process error through all handlers
   */
  private processError(report: ErrorReport): void {
    this.handlers.forEach((handler) => {
      try {
        handler(report);
      } catch (handlerError) {
        console.error('Error handler failed:', handlerError);
      }
    });
  }

  /**
   * Flush error queue when back online
   */
  private flushErrorQueue(): void {
    const queueCopy = [...this.errorQueue];
    this.errorQueue = [];

    queueCopy.forEach((report) => {
      this.processError(report);
    });
  }

  /**
   * Get error statistics for analytics
   */
  getErrorStats(): {
    sessionId: string;
    totalErrors: number;
    errorsByCategory: Record<string, number>;
    errorsBySeverity: Record<string, number>;
    recentErrors: ErrorReport[];
  } {
    const errorsByCategory: Record<string, number> = {};
    const errorsBySeverity: Record<string, number> = {};

    this.errorQueue.forEach((report) => {
      errorsByCategory[report.category] = (errorsByCategory[report.category] || 0) + 1;
      errorsBySeverity[report.severity] = (errorsBySeverity[report.severity] || 0) + 1;
    });

    return {
      sessionId: this.sessionId,
      totalErrors: this.errorQueue.length,
      errorsByCategory,
      errorsBySeverity,
      recentErrors: this.errorQueue.slice(-10), // Last 10 errors
    };
  }

  /**
   * Clear error queue (useful for testing or privacy)
   */
  clearErrors(): void {
    this.errorQueue = [];
  }

  /**
   * Set user context for error reports
   */
  setUserContext(userId: string, userEmail: string): void {
    (
      window as unknown as { __errorTrackerUserContext: { userId: string; userEmail: string } }
    ).__errorTrackerUserContext = { userId, userEmail };
  }
}

// Create singleton instance
export const errorTracker = new ErrorTrackingService();

// Export for use in error boundaries and components
export default errorTracker;
